import React, { useRef, useEffect, useState } from 'react';
import { Rect, Text, Line, Transformer, Group } from 'react-konva';
import {
  LabelElement,
  isImageElement,
  isQRCodeElement,
  isTableElement,
  isGroupElement,
  isAdvancedTableElement,
  AdvancedTableCellData,
  AdvancedTableData,
  TableElementProps,
  isLineElement, // Restored
  TableData, // Restored
} from '../../types';
import useImage from '../../hooks/useImage';
import { useElementImageGenerator } from '../../hooks/useElementImageGenerator';

// Import the new AdvancedTableElement
import AdvancedTableElement from './elements/AdvancedTableElement';
import RenderedElementContent from './RenderedElementContent';
import { SalesOrder, SalesOrderItem } from 'services/salesOrders';
import { getSOValueAtPath } from '../../helpers';
import { COMMON_RENDERED_TYPES } from '../properties/consts';

interface ElementRendererProps {
  element: LabelElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (newAttrs: Partial<LabelElement>) => void;
  previewMode?: boolean;
  previewData: SalesOrder | null;
  gridSize: number;
}

const ElementRenderer: React.FC<ElementRendererProps> = ({
  element,
  isSelected,
  onSelect,
  onChange,
  previewMode = false,
  previewData,
  gridSize,
}) => {
  const shapeRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);
  const [image] = useImage(
    isImageElement(element) ? element.src ?? null : null
  );
  const [konvaBarcodeImage, setKonvaBarcodeImage] =
    useState<HTMLImageElement | null>(null);
  const [konvaQrImage, setKonvaQrImage] = useState<HTMLImageElement | null>(
    null
  );
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);
  const [displayData, setDisplayData] = useState<AdvancedTableData | null>(
    null
  );

  const snapToGrid = (value: number): number => {
    return Math.round(value / gridSize) * gridSize;
  };

  useEffect(() => {
    if (isImageElement(element) && image) {
      setImageAspectRatio(image.width / image.height);
    }
  }, [element, image]);

  const { barcodeImageUrl, qrImageUrl } = useElementImageGenerator({
    element,
    previewMode,
    previewData,
  });

  useEffect(() => {
    if (barcodeImageUrl) {
      const img = new window.Image();
      img.onload = () => {
        setKonvaBarcodeImage(img);
      };
      img.src = barcodeImageUrl;
    } else {
      setKonvaBarcodeImage(null);
    }
  }, [barcodeImageUrl]);

  useEffect(() => {
    if (qrImageUrl) {
      const img = new window.Image();
      img.onload = () => {
        setKonvaQrImage(img);
      };
      img.src = qrImageUrl;
    } else {
      setKonvaQrImage(null);
    }
  }, [qrImageUrl]);

  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  useEffect(() => {
    if (
      previewMode &&
      isAdvancedTableElement(element) &&
      element.advancedTableData &&
      previewData?.salesItems
    ) {
      const originalTableData = element.advancedTableData;
      const rowCount = previewData.salesItems.length;
      const headers = originalTableData.headers;
      const tableData: AdvancedTableCellData[][] = [];

      if (headers) {
        previewData.salesItems.forEach((item: SalesOrderItem) => {
          const rowData: AdvancedTableCellData[] = [];
          headers.forEach((header) => {
            const content = getSOValueAtPath(
              item,
              header.path || header.key || ''
            );
            rowData.push({
              content: content?.toString() ?? '',
            });
          });
          tableData.push(rowData);
        });
      }

      setDisplayData({
        ...originalTableData,
        numRows: rowCount,
        numColumns: headers?.length ?? 0,
        rowHeights: Array(rowCount).fill(
          originalTableData.rowHeights?.[0] || 18
        ),
        data: tableData,
      });
    } else if (isAdvancedTableElement(element)) {
      setDisplayData(null);
    }
  }, [element, previewData, previewMode]);

  const handleDragEnd = (e: any) => {
    const node = e.target;
    onChange({
      x: snapToGrid(node.x()),
      y: snapToGrid(node.y()),
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    node.scaleX(1);
    node.scaleY(1);

    let newWidth = Math.max(gridSize, snapToGrid(node.width() * scaleX));
    let newHeight = Math.max(gridSize, snapToGrid(node.height() * scaleY));

    if (isImageElement(element) && imageAspectRatio) {
      if (Math.abs(scaleX - scaleY) > 0.1) {
        if (scaleX > scaleY) {
          newHeight = Math.round(newWidth / imageAspectRatio);
        } else {
          newWidth = Math.round(newHeight * imageAspectRatio);
        }
      } else {
        newHeight = Math.round(newWidth / imageAspectRatio);
      }

      if (newWidth < gridSize) {
        newWidth = gridSize;
        newHeight = Math.round(gridSize / imageAspectRatio);
      }
      if (newHeight < gridSize) {
        newHeight = gridSize;
        newWidth = Math.round(gridSize * imageAspectRatio);
      }
    } else if (isQRCodeElement(element)) {
      const size = Math.max(newWidth, newHeight);
      newWidth = size;
      newHeight = size;
    } else if (isLineElement(element)) {
      newHeight = element.lineWidth || 1; // If it's a line element, it has lineWidth from ShapeElementProps
    } else if (isTableElement(element)) {
      const tableElement = element as TableElementProps;
      const tableData = tableElement.tableData
        ? tableElement.tableData
        : ({
            rows: 3,
            columns: 3,
            showGrid: true,
            borderColor: '#000000',
            borderWidth: 1,
            headerBackgroundColor: '#ffffff',
            headerTextColor: '#000000',
            backgroundColor: '#ffffff',
            highlightedRows: [],
            highlightedColumns: [],
            highlightColor: '#f0f0f0',
          } as TableData);

      // Calculate cell dimensions if not already set
      const cellWidth =
        tableData.cellWidth || element.width / tableData.columns;
      const cellHeight =
        tableData.cellHeight || element.height / tableData.rows;

      // Update tableData with calculated dimensions
      tableData.cellWidth = cellWidth;
      tableData.cellHeight = cellHeight;

      const newCellWidth = Math.floor(newWidth / tableData.columns);
      const newCellHeight = Math.floor(newHeight / tableData.rows);

      // Preserve all existing table properties while updating dimensions
      const updatedTableData = {
        ...tableData,
        cellWidth: newCellWidth,
        cellHeight: newCellHeight,
        showGrid: tableData.showGrid !== undefined ? tableData.showGrid : true,
      };

      // Update the element with new dimensions and preserved table data
      onChange({
        x: snapToGrid(node.x()),
        y: snapToGrid(node.y()),
        width: newWidth,
        height: newHeight,
        rotation: Math.round(node.rotation() / 15) * 15,
        tableData: updatedTableData,
      });
      return;
    }

    onChange({
      x: snapToGrid(node.x()),
      y: snapToGrid(node.y()),
      width: newWidth,
      height: newHeight,
      rotation: Math.round(node.rotation() / 15) * 15,
    });
  };

  const commonProps = {
    ref: shapeRef,
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    rotation: element.rotation || 0,
    draggable: !previewMode,
    onClick: onSelect,
    onTap: onSelect,
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
    onMouseEnter: (e: any) => {
      const container = e.target.getStage().container();
      container.style.cursor = 'move';
    },
    onMouseLeave: (e: any) => {
      const container = e.target.getStage().container();
      container.style.cursor = 'default';
    },
  };

  const renderTable = () => {
    if (!isTableElement(element)) return null;

    const tableData = element.tableData || {
      rows: 3,
      columns: 3,
      cellWidth: element.width / 3,
      cellHeight: element.height / 3,
      headerRow: false,
      headerRowHeight: 40,
      headerFontSize: 16,
      headerFontFamily: 'Arial',
      headerBackgroundColor: '#f0f0f0',
      headerTextColor: '#000000',
      headerTexts: Array(3).fill('Header'),
      borderColor: element.stroke || '#000000',
      borderWidth: element.lineWidth || 1,
      showGrid: true,
      backgroundColor: '#ffffff',
      highlightedRows: [],
      highlightedColumns: [],
      highlightColor: undefined,
    };

    // Ensure cell dimensions are set
    const cellWidth = tableData.cellWidth || element.width / tableData.columns;
    const cellHeight = tableData.cellHeight || element.height / tableData.rows;

    // Update tableData with calculated dimensions
    tableData.cellWidth = cellWidth;
    tableData.cellHeight = cellHeight;

    const cells = [];

    // Add a transparent background rect to make the entire table area draggable
    cells.push(
      <Rect
        key="draggable-area"
        x={0}
        y={0}
        width={element.width}
        height={element.height}
        fill="transparent"
      />
    );

    if (tableData.backgroundColor) {
      cells.push(
        <Rect
          key="background"
          x={0}
          y={0}
          width={element.width}
          height={element.height}
          fill={tableData.backgroundColor}
        />
      );
    }

    if (tableData.headerRow) {
      cells.push(
        <Rect
          key="header-background"
          x={0}
          y={0}
          width={element.width}
          height={tableData.headerRowHeight || tableData.cellHeight}
          fill={tableData.headerBackgroundColor || '#f0f0f0'}
        />
      );

      const headerTexts =
        tableData.headerTexts || Array(tableData.columns).fill('Header');
      headerTexts.forEach((text, index) => {
        cells.push(
          <Text
            key={`header-text-${index}`}
            x={
              index * (tableData.cellWidth || element.width / tableData.columns)
            }
            y={0}
            width={tableData.cellWidth || element.width / tableData.columns}
            height={tableData.headerRowHeight || tableData.cellHeight}
            text={text}
            fontSize={tableData.headerFontSize || 16}
            fontFamily={tableData.headerFontFamily || 'Arial'}
            fill={tableData.headerTextColor || '#000000'}
            align="center"
            verticalAlign="middle"
          />
        );
      });
    }

    if (tableData.highlightedRows?.length) {
      tableData.highlightedRows.forEach((rowIndex) => {
        const yOffset =
          tableData.headerRow && rowIndex > 0
            ? (tableData.headerRowHeight ||
                tableData.cellHeight ||
                element.height / tableData.rows) +
              (rowIndex - 1) *
                (tableData.cellHeight || element.height / tableData.rows)
            : rowIndex *
              (tableData.cellHeight || element.height / tableData.rows);

        cells.push(
          <Rect
            key={`highlight-row-${rowIndex}`}
            x={0}
            y={yOffset}
            width={element.width}
            height={tableData.cellHeight || element.height / tableData.rows}
            fill={tableData.highlightColor || '#f0f0f0'}
          />
        );
      });
    }

    if (tableData.highlightedColumns?.length) {
      tableData.highlightedColumns.forEach((colIndex) => {
        cells.push(
          <Rect
            key={`highlight-col-${colIndex}`}
            x={
              colIndex *
              (tableData.cellWidth || element.width / tableData.columns)
            }
            y={0}
            width={tableData.cellWidth || element.width / tableData.columns}
            height={element.height}
            fill={tableData.highlightColor || '#f0f0f0'}
          />
        );
      });
    }

    if (tableData.showGrid) {
      for (let row = 0; row <= tableData.rows; row++) {
        const y =
          row === 1 && tableData.headerRow
            ? tableData.headerRowHeight ||
              tableData.cellHeight ||
              element.height / tableData.rows
            : row * (tableData.cellHeight || element.height / tableData.rows);

        cells.push(
          <Line
            key={`h-${row}`}
            points={[0, y, element.width, y]}
            stroke={tableData.borderColor}
            strokeWidth={tableData.borderWidth}
          />
        );
      }

      // Regular equal columns
      for (let col = 0; col <= tableData.columns; col++) {
        cells.push(
          <Line
            key={`v-${col}`}
            points={[
              col * (tableData.cellWidth || element.width / tableData.columns),
              0,
              col * (tableData.cellWidth || element.width / tableData.columns),
              element.height,
            ]}
            stroke={tableData.borderColor || '#000000'}
            strokeWidth={tableData.borderWidth || 1}
          />
        );
      }
    } else {
      cells.push(
        <Rect
          key="outer-border"
          x={0}
          y={0}
          width={element.width}
          height={element.height}
          stroke={tableData.borderColor}
          strokeWidth={tableData.borderWidth}
          fill="transparent"
        />
      );
    }

    return <Group {...commonProps}>{cells}</Group>;
  };

  const renderElement = () => {
    if (COMMON_RENDERED_TYPES.includes(element.type)) {
      let imageProps = {};
      if (element.type === 'barcode') {
        imageProps = { konvaBarcodeImage };
      } else if (element.type === 'qrcode') {
        imageProps = { konvaQrImage };
      } else if (element.type === 'image') {
        imageProps = { image };
      }

      return (
        <RenderedElementContent
          element={element}
          commonProps={commonProps}
          previewMode={previewMode}
          previewData={previewData}
          {...imageProps}
        />
      );
    } else if (element.type === 'table' && isTableElement(element)) {
      return renderTable();
    } else if (
      element.type === 'advancedTable' &&
      isAdvancedTableElement(element)
    ) {
      return (
        <AdvancedTableElement
          {...commonProps}
          id={element.id}
          type={element.type}
          advancedTableData={
            previewMode && displayData ? displayData : element.advancedTableData
          }
          onUpdateElement={onChange}
          previewMode={previewMode}
        />
      );
    }

    return null;
  };

  return (
    <>
      {renderElement()}
      {isSelected && !previewMode && (
        <Transformer
          ref={transformerRef}
          boundBoxFunc={(oldBox, newBox) => {
            const snappedBox = {
              ...newBox,
              x: snapToGrid(newBox.x),
              y: snapToGrid(newBox.y),
              width: snapToGrid(newBox.width),
              height: snapToGrid(newBox.height),
            };

            if (isQRCodeElement(element)) {
              const size = Math.max(snappedBox.width, snappedBox.height);
              return {
                ...snappedBox,
                width: size,
                height: size,
              };
            } else if (isLineElement(element)) {
              return {
                ...snappedBox,
                height: element.lineWidth || 1,
              };
            } else if (isImageElement(element) && imageAspectRatio) {
              const width = snappedBox.width;
              const height = Math.round(width / imageAspectRatio);
              return {
                ...snappedBox,
                height,
              };
            }

            if (snappedBox.width < gridSize || snappedBox.height < gridSize) {
              return oldBox;
            }
            return snappedBox;
          }}
          rotationSnaps={[
            0, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180,
          ]}
          enabledAnchors={
            isQRCodeElement(element)
              ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
              : isLineElement(element)
              ? ['middle-left', 'middle-right']
              : isImageElement(element)
              ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
              : isGroupElement(element)
              ? []
              : isAdvancedTableElement(element)
              ? []
              : [
                  'top-left',
                  'top-center',
                  'top-right',
                  'middle-left',
                  'middle-right',
                  'bottom-left',
                  'bottom-center',
                  'bottom-right',
                ]
          }
          rotateEnabled={true}
        />
      )}
    </>
  );
};

export default ElementRenderer;
